import React, { useEffect, useState } from "react";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { ref, get, child } from "firebase/database";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import AttendanceTable from "../components/Table/AttendanceTable";
import ModalInstitutionNotice from "./ModalInstitutionNotice";
import {
  listarInstitutionNotices,
  crearInstitutionNotice,
  actualizarInstitutionNotice,
  eliminarInstitutionNotice,
} from "../services/institutionNoticesService";
import { sendInstitutionNoticePush } from "../services/pushNotificationsService";
import { dbAsistencia } from "../firebase";
import "../styles/agregarUsuario.css";

function fmtDate(ms) {
  if (!Number.isFinite(Number(ms))) return "-";
  return new Date(Number(ms)).toLocaleString("es-PE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fireSwal(options) {
  const userDidOpen = options?.didOpen;
  return Swal.fire({
    ...options,
    didOpen: (popup) => {
      const container = Swal.getContainer();
      if (container) {
        container.style.zIndex = "99999";
      }
      if (typeof userDidOpen === "function") {
        userDidOpen(popup);
      }
    },
  });
}

export default function InstitutionNotices() {
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);

  const loadRows = async () => {
    setLoadingList(true);
    try {
      const data = await listarInstitutionNotices();
      setRows(data);
    } catch (e) {
      console.error("Error loading notices:", e);
      setNotice("No se pudo cargar institution_notices.");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadRows();

    const loadRecipients = async () => {
      try {
        const rootRef = ref(dbAsistencia);
        const snap = await get(child(rootRef, "users"));
        if (!snap.exists()) {
          setRecipients([]);
          return;
        }

        const data = snap.val() || {};
        const designationSet = new Set();
        const list = Object.entries(data)
          .map(([id, u]) => ({
            id,
            name: u?.Name || "",
            uid: u?.UID ?? "",
            dni: u?.UUID ?? "",
            designation: u?.designation || "",
            active: typeof u?.active === "boolean" ? u.active : true,
            hasToken: Boolean(
              (typeof u?.notificationToken === "string" && u.notificationToken.trim()) ||
                (u?.fcmTokens &&
                  typeof u.fcmTokens === "object" &&
                  Object.keys(u.fcmTokens).length)
            ),
          }))
          .filter((u) => u.active && u.hasToken);

        for (const u of list) {
          if (u.designation) designationSet.add(String(u.designation).trim());
        }
        setDesignationOptions(
          Array.from(designationSet)
            .filter(Boolean)
            .sort()
            .map((key) => ({ key, label: key }))
        );
        setRecipients(list);
      } catch (e) {
        console.error("Error loading recipients:", e);
      }
    };

    loadRecipients();
  }, []);

  const handleConfirmModal = async (payload, { notify = true } = {}) => {
    if (saving) return;

    if (notify) {
      const isSegmented =
        Array.isArray(payload?.designations) && payload.designations.length > 0;
      const targetText = isSegmented
        ? `Esta notificacion se enviara segmentada a: ${payload.designations.join(", ")}.`
        : "Esta notificacion se enviara a TODOS los usuarios.";

      const confirmRes = await fireSwal({
        icon: "question",
        title: "Confirmar envio",
        text: targetText,
        showCancelButton: true,
        confirmButtonText: "Si, enviar",
        cancelButtonText: "Cancelar",
      });
      if (!confirmRes?.isConfirmed) return;
    }

    setSaving(true);
    fireSwal({
      title: notify ? "Enviando notificacion..." : "Guardando notificacion...",
      text: notify ? "Procesando guardado y push" : "Guardando cambios sin notificar",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    try {
      const designationsLabel =
        Array.isArray(payload?.designations) && payload.designations.length
          ? ` Designaciones: ${payload.designations.join(", ")}.`
          : "";
      let pushResp = null;
      if (editingNotice?.id) {
        await actualizarInstitutionNotice(editingNotice.id, payload);
        if (notify) {
          pushResp = await sendInstitutionNoticePush(payload);
          if (pushResp?.ok) {
            await fireSwal({
              icon: "success",
              title: "Notificacion actualizada",
              text: `Enviada (${pushResp.successCount} ok / ${pushResp.failureCount} fail).${designationsLabel}`,
            });
          } else if (pushResp?.skipped) {
            await fireSwal({
              icon: "warning",
              title: "Notificacion actualizada",
              text: "No se envio push (falta REACT_APP_PUSH_BACKEND_URL).",
            });
          } else {
            await fireSwal({
              icon: "success",
              title: "Notificacion actualizada",
            });
          }
        } else {
          await fireSwal({
            icon: "success",
            title: "Notificacion actualizada",
            text: "Se guardo sin notificar.",
          });
        }
      } else {
        await crearInstitutionNotice(payload);
        if (notify) {
          pushResp = await sendInstitutionNoticePush(payload);
          if (pushResp?.ok) {
            await fireSwal({
              icon: "success",
              title: "Notificacion creada",
              text: `Enviada (${pushResp.successCount} ok / ${pushResp.failureCount} fail).${designationsLabel}`,
            });
          } else if (pushResp?.skipped) {
            await fireSwal({
              icon: "warning",
              title: "Notificacion creada",
              text: "No se envio push (falta REACT_APP_PUSH_BACKEND_URL).",
            });
          } else {
            await fireSwal({
              icon: "success",
              title: "Notificacion creada",
            });
          }
        } else {
          await fireSwal({
            icon: "success",
            title: "Notificacion creada",
            text: "Se guardo sin notificar.",
          });
        }
      }

      setIsModalOpen(false);
      setEditingNotice(null);
      await loadRows();
    } catch (e) {
      console.error("Error saving notice:", e);
      await fireSwal({
        icon: "error",
        title: "Error",
        text: e?.message || "No se pudo guardar/enviar la notificacion.",
      });
    } finally {
      Swal.close();
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const ok = window.confirm(
      `Se eliminara la notificacion "${row?.title || "sin titulo"}". Deseas continuar?`
    );
    if (!ok) return;
    try {
      await eliminarInstitutionNotice(row?.id);
      setNotice("Notificacion eliminada.");
      await loadRows();
    } catch (e) {
      console.error("Error deleting notice:", e);
      setNotice("No se pudo eliminar la notificacion.");
    }
  };

  const columns = [
    { key: "title", header: "Titulo", width: "20%", ellipsis: true },
    { key: "body", header: "Mensaje", width: "36%", ellipsis: true },
    { key: "level", header: "Nivel", width: "10%", align: "center" },
    {
      key: "enabled",
      header: "Estado",
      width: "10%",
      align: "center",
      render: (r) => (
        <span
          className={`state-badge ${
            r.enabled ? "state-badge--active" : "state-badge--inactive"
          }`}
        >
          {r.enabled ? "Activo" : "Inactivo"}
        </span>
      ),
    },
    { key: "startAt", header: "Inicio", width: "12%", render: (r) => fmtDate(r.startAt) },
    { key: "endAt", header: "Fin", width: "12%", render: (r) => fmtDate(r.endAt) },
  ];

  const actions = (row) => (
    <div className="user-actions">
      <button
        className="icon-action-btn"
        onClick={() => {
          setEditingNotice(row);
          setIsModalOpen(true);
        }}
        title="Editar notificacion"
        aria-label="Editar notificacion"
      >
        <FiEdit2 />
      </button>
      <button
        className="icon-action-btn"
        onClick={() => handleDelete(row)}
        title="Eliminar"
        aria-label="Eliminar"
      >
        <FiTrash2 />
      </button>
    </div>
  );

  const toolbarRight = (
    <div style={{ display: "flex", gap: 8 }}>
      <button
        className="btn ghost"
        onClick={() => {
          setEditingNotice(null);
          setIsModalOpen(true);
        }}
        disabled={saving}
      >
        + Nueva notificacion
      </button>
    </div>
  );

  return (
    <div className="add-user-page">
      <div className="add-user-card">
        <div className="user-list-meta">
          <span>Mostrando {rows.length} notificacion(es)</span>
          {notice ? <span className="user-notice">{notice}</span> : null}
        </div>

        <AttendanceTable
          columns={columns}
          rows={rows}
          loading={loadingList}
          emptyText="Sin notificaciones"
          actions={rows.length ? actions : null}
          toolbarRight={toolbarRight}
          getRowKey={(r) => r.id}
          dense={false}
        />
      </div>

      <ModalInstitutionNotice
        open={isModalOpen}
        mode={editingNotice?.id ? "edit" : "create"}
        initialData={editingNotice}
        recipients={recipients}
        designationOptions={designationOptions}
        submitting={saving}
        onClose={() => {
          if (saving) return;
          setIsModalOpen(false);
          setEditingNotice(null);
        }}
        onConfirm={handleConfirmModal}
      />
    </div>
  );
}
